import * as React from 'react';

import classNames from 'classnames';

import {CloseButton} from 'shells/button/close-button/close-button';
import {Sidebar} from 'shells/sidebar/sidebar';
import {Body} from 'shells/typography/body';
import {AddablePeople} from 'shells/visitor-sidebar/visitor-content/company-people/addable-people';

import type {McfxViqContactFragment as Contact} from 'nutshell-graphql-types';

import {AddFooter} from './add-footer';
import {useGetUnlockInfo} from './graphql/hooks/use-get-unlock-info';
import {HideRecommendationsButton} from './hide-recommendations-button';

import styles from './people-iq-sidebar.module.css';

const PEOPLE_IQ_SETTINGS_URL_PATH = '/setup/people-iq';

type Props = {
    contacts: Contact[];
    companyName: string;
    onHideFutureRecommendations: () => void;
    onAddContacts: (contactIds: string[]) => void;
    onClose: () => void;
};

export function PeopleIqSidebar(props: Props) {
    const [selectedPersonIds, setSelectedPersonIds] = React.useState<string[]>([]);
    const [isSaving, setIsSaving] = React.useState<boolean>(false);
    const {unlockInfo} = useGetUnlockInfo();

    // @ts-expect-error
    const onUpdateSelectedPersonIds = (personId) =>
        setSelectedPersonIds(
            selectedPersonIds.includes(personId)
                ? selectedPersonIds.filter((id) => id !== personId)
                : [...selectedPersonIds, personId]
        );

    const onAddContacts = () => {
        setIsSaving(true);

        props.onAddContacts(selectedPersonIds);
    };

    const firstContact = props.contacts.find((contact) => contact.id === selectedPersonIds[0]);
    const addablePeople = props.contacts.map((contact) => ({
        id: contact.id,
        name: contact.fullName,
        location: contact.location,
        jobTitle: contact.jobTitle,
        position: contact.position,
        hasEmail: contact.hasEmail,
        hasPhone: contact.hasPhone,
        hasLinkedinUrl: contact.hasLinkedinUrl,
    }));

    const scrollContainerClassName = classNames(styles['scroll-container'], {
        [styles['scroll-container-with-add-footer']]: selectedPersonIds.length > 0,
    });

    return (
        <Sidebar isOpen={true} onClose={props.onClose}>
            <div className={styles.container}>
                <div className={styles.header}>
                    <div>
                        <div className='mb-4'>
                            <Body size='large' isBold={true}>
                                Add people to {props.companyName}
                            </Body>
                        </div>
                        <Body isItalic={true} size='small'>
                            PeopleIQ has found people that might work at this company.
                        </Body>
                    </div>
                    <CloseButton onClick={props.onClose} />
                </div>
                <div className={scrollContainerClassName}>
                    <AddablePeople
                        people={addablePeople}
                        selectedPersonIds={selectedPersonIds}
                        updateSelectedPersonIds={onUpdateSelectedPersonIds}
                        onClose={props.onClose}
                        adjustFiltersUrl={PEOPLE_IQ_SETTINGS_URL_PATH}
                    />
                    <div className={styles.footer}>
                        <HideRecommendationsButton
                            onHideFutureRecommendations={() => {
                                props.onHideFutureRecommendations();
                                props.onClose();
                            }}
                            companyName={props.companyName}
                        />
                    </div>
                </div>
                {selectedPersonIds.length ? (
                    <AddFooter
                        firstContactName={firstContact ? firstContact.fullName : 'Unknown'}
                        onAddContacts={onAddContacts}
                        numSelectedContacts={selectedPersonIds.length}
                        unlocksRemaining={unlockInfo ? unlockInfo.unlocksRemaining : 0}
                        isSaving={isSaving}
                    />
                ) : undefined}
            </div>
        </Sidebar>
    );
}
